import { useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { REPORT_CONSTANTS, NON_ENCRYPTED_KEYS } from "~/helpers/constants";
import { createErrorHandler } from "~/helpers/errorHandling.helper";
import { encryptReportPayload } from "~/helpers/encryption.helper";
import { resolveReportDekEnvelope } from "~/helpers/reportDekEnvelope.helper";
import {
  enqueuePartialSave,
  markReportTerminal,
  type PartialSaveJob,
} from "~/helpers/reportPartialSaveQueue.helper";
import {
  base64ToBuffer,
  bufferToBase64,
  generatePerReportDek,
  unwrapDekFromUser,
  wrapDekForUser,
} from "~/helpers/envelopeCrypto";
import { importRsaPublicKey, rsaOaepWrapDek } from "~/helpers/orgCrypto";
import { useOrgPublicKey } from "~/hooks/useOrgEncryption";
import { encryptionWorker } from "~/services/workers/encryptionWorker.service";
import { addNewReport } from "~/services/gateways/report.gateway";
import { usePassword } from "~/store/credentials";
import { SNAP_PROGRESS_KEY, type SnapProgressMetadata } from "~/types/report-progress";
import type { ReportData } from "~/types/global";

/**
 * Save de um snapshot cumulativo parcial (épico #21124, task #21220).
 *
 * O frontend faz exatamente duas coisas com o parcial: **renderiza** e
 * **encripta o que recebeu**. Nada de merge, dedup, releitura do store ou
 * persistência por seção — a acumulação e o dedup por identidade de item já
 * aconteceram no servidor.
 *
 * Diferenças deliberadas para `addNewReportMutation` (por isso este caminho é
 * dedicado, e não uma reutilização):
 *
 * - **reusa o DEK do relatório** (`user_wrapped_dek`/`user_dek_iv`) quando um
 *   já existe — `generateEnvelope()` (que sempre minta um DEK NOVO) nunca é
 *   chamado. Bug encontrado ao vivo 2026-08-19 ("lembre-se do stream"):
 *   `resolveReportDekEnvelope` só resolve um DEK JÁ EXISTENTE — um relatório
 *   que nunca foi salvo nem uma vez (toda a janela entre a criação e o save
 *   terminal, que é o único outro lugar que minta um) não tem DEK em lugar
 *   nenhum, então TODO save de parcial era silenciosamente ignorado o tempo
 *   todo (confirmado: `useReportPartialSave` logava "Envelope de DEK
 *   indisponível" pra cada um dos 5 parciais de um relatório novo, e
 *   `user_reports.data` ficava vazio). Agora minta uma única vez quando
 *   `resolveReportDekEnvelope` devolve `null` — mesma primitiva de baixo
 *   nível que `addNewReportMutation` usa — e cacheia client-side
 *   (`dekCacheRef`) pelo resto da sessão, pra todo save seguinte deste
 *   relatório reusar DETERMINISTICAMENTE o mesmo DEK em vez de mintar de
 *   novo (mintar a cada save dessincronizaria o ciphertext de
 *   `user_wrapped_dek` entre saves, já que cada um só seria decriptável com
 *   o DEK vivo no banco NAQUELE momento);
 * - **não força status terminal**: envia `report_status` estruturado e
 *   NÃO-terminal (`{status_report: "pending", snap_progress}`), em texto claro;
 * - **não recalcula `data_count`** nem toca em `report_search_args`, e descarta
 *   um `report_name` degradado (ver `FIELDS_NOT_SENT` e
 *   `DEGRADED_REDES_SOCIAIS_NAME`);
 * - **não envia HMAC/n-grams** — os HMACs de busca são gravados na criação e o
 *   save terminal os reescreve. Isso também garante, por construção, que o
 *   metadado de progresso jamais entra na entrada de n-grams;
 * - **não envia as chaves de envelope**, então o banco preserva
 *   `user_wrapped_dek`/`user_dek_iv`/`org_wrapped_dek`/`key_version`.
 *
 * Segurança: o texto claro do relatório nunca cruza o `PUT` (só o ciphertext),
 * nunca vai para local/session storage e nunca é logado. `report_status` e
 * `snap_progress` são metadado operacional NÃO-sensível e seguem em texto claro
 * (estão em `NON_ENCRYPTED_KEYS`).
 */

export interface PartialSaveInput {
  userReportsId: string;
  sequence: number;
  /** `result.data` do frame `report_partial` — o relatório canônico cumulativo. */
  snapshot: Record<string, unknown>;
  progress: SnapProgressMetadata | null;
}

/** Campos que o servidor deriva/possui e que um save de parcial não reenvia.
 *
 * `report_search_args` entra aqui porque o envelope parcial o carrega vazio
 * quando o backend não tem o plaintext da entrada em memória (cache de nome
 * process-local, miss entre workers). Reenviá-lo apagava o que a criação
 * gravou — e a entrada original não é recuperável, porque a url durável está
 * encriptada. O save terminal é o único que reescreve esse campo. */
const FIELDS_NOT_SENT = new Set([
  "created_at",
  "modified_at",
  "related_documents",
  "report_search_args",
  "user_wrapped_dek",
  "user_dek_iv",
  "org_wrapped_dek",
  "key_version",
]);

/** Nome de `redes_sociais` sem a entrada — `"redes_sociais"`, `"redes_sociais #"`,
 * `"redes_sociais #4"`. Vem do mesmo miss de cache acima; sobrescrever com ele
 * apaga um nome que já carregava a URL/handle. */
const DEGRADED_REDES_SOCIAIS_NAME = /^redes_sociais\s*(#\s*\d*)?$/i;

function isDegradedReportName(reportType: unknown, reportName: unknown): boolean {
  if (reportType !== REPORT_CONSTANTS.types.redes_sociais) return false;
  return typeof reportName === "string" && DEGRADED_REDES_SOCIAIS_NAME.test(reportName.trim());
}

/** `report_status` estruturado e não-terminal, em texto claro. */
export function buildPendingReportStatus(
  progress: SnapProgressMetadata | null,
): Record<string, unknown> {
  const status: Record<string, unknown> = {
    [REPORT_CONSTANTS.new_report.status_report]: REPORT_CONSTANTS.status.pending,
  };
  // Pendente sem progresso conhecido continua válido — o campo é opcional.
  if (progress) status[SNAP_PROGRESS_KEY] = progress;
  return status;
}

export function useReportPartialSave() {
  const queryClient = useQueryClient();
  const secretKey = usePassword();
  const { data: orgPublicKey } = useOrgPublicKey();
  // Per-report DEK cache — ONLY for a DEK this hook itself minted (see
  // runSave). Never populated from `resolveReportDekEnvelope`'s result: that
  // path is re-resolved every save on purpose, so a password reset's DEK
  // re-wrap is picked up immediately instead of reusing a stale unwrap.
  const mintedDekCacheRef = useRef<Map<string, ArrayBuffer>>(new Map());

  const runSave = useCallback(
    async (job: PartialSaveJob) => {
      const { reportId, snapshot, progress } = job;

      if (!secretKey) {
        // Sem a chave do usuário não há como desembrulhar o DEK do relatório.
        // Pular é correto: nunca cair para uma chave insegura.
        console.warn(
          `[useReportPartialSave] Sem secretKey em memória — save do parcial ignorado (report=${reportId})`,
        );
        return;
      }

      const rawUserKey = base64ToBuffer(secretKey);
      const userKeyBuffer = rawUserKey.buffer.slice(
        rawUserKey.byteOffset,
        rawUserKey.byteOffset + rawUserKey.byteLength,
      ) as ArrayBuffer;

      let dekBuffer: ArrayBuffer;
      let envelopeFieldsToSend: Record<string, unknown> = {};

      const cachedMintedDek = mintedDekCacheRef.current.get(reportId);
      if (cachedMintedDek) {
        dekBuffer = cachedMintedDek;
      } else {
        const envelope = await resolveReportDekEnvelope(reportId, queryClient);
        if (envelope) {
          const dek = await unwrapDekFromUser(userKeyBuffer, envelope.wrappedDek, envelope.dekIv);
          dekBuffer = dek.buffer.slice(
            dek.byteOffset,
            dek.byteOffset + dek.byteLength,
          ) as ArrayBuffer;
        } else {
          // No DEK anywhere yet — this is the FIRST save this report has ever
          // had (see docstring). Mint once, from the same low-level
          // envelopeCrypto.ts/orgCrypto.ts primitives `addNewReportMutation`
          // uses, and send the envelope fields THIS one time so the DB has
          // something for the next partial (or the terminal save) to find.
          if (orgPublicKey) {
            const dek = generatePerReportDek();
            const wrapped = await wrapDekForUser(userKeyBuffer, dek);
            const rsaPubKey = await importRsaPublicKey(orgPublicKey.public_key);
            const orgWrappedBuf = await rsaOaepWrapDek(rsaPubKey, dek);
            dekBuffer = dek.buffer as ArrayBuffer;
            envelopeFieldsToSend = {
              user_wrapped_dek: wrapped.user_wrapped_dek,
              user_dek_iv: wrapped.user_dek_iv,
              org_wrapped_dek: bufferToBase64(new Uint8Array(orgWrappedBuf)),
              key_version: orgPublicKey.key_version,
            };
          } else {
            // Standalone/legacy account, no org key — same fallback
            // `addNewReportMutation` uses: encrypt directly with the user's
            // own secret key, no envelope fields to send.
            dekBuffer = userKeyBuffer;
          }
          mintedDekCacheRef.current.set(reportId, dekBuffer);
        }
      }

      const body: Record<string, unknown> = {};
      const reportType = (snapshot as Record<string, unknown> | undefined)?.report_type;
      for (const [key, value] of Object.entries(snapshot ?? {})) {
        if (FIELDS_NOT_SENT.has(key)) continue;
        if (key === "report_name" && isDegradedReportName(reportType, value)) continue;
        body[key] = value;
      }
      body[REPORT_CONSTANTS.new_report.report_status] = buildPendingReportStatus(progress);

      // Criptografia pesada fora da main thread — mesma primitiva AES-GCM que o
      // save terminal usa, só que com o DEK JÁ EXISTENTE (ou recém-mintado) do
      // relatório.
      //
      // Bug encontrado ao vivo 2026-08-19 (mesma sessão do "lembre-se do
      // stream"): `encryptReportPayload` espera que o `encryptData` injetado
      // devolva o contrato `CryptoResult<EncryptedPayload>`
      // (`{success, data: {encrypted, iv}}` — o mesmo formato de
      // `useEncryption.ts`'s `_encryptData`) e extrai `encryptedValue.data`
      // pra cada campo. `encryptionWorker.encrypt(...)` resolve DIRETO pra
      // `{encrypted, iv}`, sem esse envelope — então `encryptedValue.data`
      // saía `undefined` pra TODO campo encriptado (todos exceto
      // `NON_ENCRYPTED_KEYS`), e `JSON.stringify` descarta chaves com valor
      // `undefined`. Confirmado ao vivo: o PUT saía só com `report_type`,
      // `user_reports_id`, `subject_age`, `report_status` — `report_name`,
      // `subject_name` e, criticamente, `data` (o conteúdo do relatório)
      // nunca chegavam ao backend, em TODO save de parcial, mesmo já com DEK
      // resolvido corretamente.
      const encrypted = (await encryptReportPayload(
        body as never,
        null,
        async (data: unknown) => ({
          success: true,
          data: await encryptionWorker.encrypt(data, dekBuffer),
        }),
        NON_ENCRYPTED_KEYS,
      )) as ReportData;

      await addNewReport({ ...encrypted, ...envelopeFieldsToSend } as ReportData, reportId);
    },
    [queryClient, secretKey, orgPublicKey],
  );

  const savePartialReportMutation = useMutation({
    mutationFn: async (input: PartialSaveInput) => {
      await enqueuePartialSave(
        {
          reportId: input.userReportsId,
          sequence: input.sequence,
          snapshot: input.snapshot,
          progress: input.progress,
        },
        async (job) => {
          try {
            await runSave(job);
          } catch (error) {
            // Falha preserva o estado anterior no banco, o snapshot renderizado
            // e o progresso em memória; nenhuma cache é invalidada de forma
            // destrutiva. O próximo parcial (ou o replay) tenta de novo.
            toast.error("Erro ao salvar o relatório parcial", {
              description:
                "O relatório continua sendo coletado; a próxima atualização tentará salvar novamente.",
            });
            createErrorHandler(
              "Erro ao salvar o relatório parcial",
              "Erro ao salvar",
            )(error as never);
            throw error;
          }
        },
      );
    },
  });

  return { savePartialReportMutation, markReportTerminal };
}
